import {
  subscribe as subscribeToBroadcastEvent,
  subscribeToConnectionStatus as subscribeToConnectionStatusDefault,
} from '../../../app/etc/broadcast-event-bus';
import type {
  QueryClient,
  QueryKey,
  QueryObserverOptions,
  QueryObserverResult,
} from '@tanstack/react-query';
import { QueryObserver } from '@tanstack/react-query';
import {
  createStore,
  Atom,
  atom,
  WritableAtom,
  Provider,
  useAtom,
  Getter,
} from 'jotai';
import React from 'react';

const store = createStore();

export type StaleState = 'stale' | 'ok';

export type ReadonlyWatchable<T> = {
  map: <U>(cb: (t: T) => U) => ReadonlyWatchable<U>;
  snapshot(): T;
  asAtom(): Atom<T>;
  lastUpdateTime: number;
  subscribe(cb: (t: T) => void): () => void;
};

export class Watchable<T> implements ReadonlyWatchable<T> {
  private _atom: WritableAtom<T, [T], unknown>;
  private _equalityFn: ((a: T, b: T) => boolean) | undefined;
  private _updater: (value: T) => T = (x) => x;

  lastUpdateTime: number = Date.now();

  protected constructor(
    baseAtom: (watchable: Watchable<T>) => WritableAtom<T, [T], unknown>,
    equalityFn?: (a: T, b: T) => boolean,
    updater?: (value: T) => T
  ) {
    this._atom = baseAtom(this);
    this._equalityFn = equalityFn ?? ((_a, _b) => false);
    this._updater = updater ?? ((x) => x);
  }

  static fromValue<T>(
    value: T,
    opts?: {
      updater?: (value: T) => T;
      equalityFn?: (a: T, b: T) => boolean;
    }
  ): Watchable<T> {
    return new Watchable<T>(() => atom(value), opts?.equalityFn, opts?.updater);
  }

  // Static method to create derived watchables
  static from<T>(
    deriveFn: (get: <U>(watchable: ReadonlyWatchable<U>) => U) => T,
    opts?: {
      updater?: (value: T) => T;
    }
  ): ReadonlyWatchable<T> {
    const sourceWatchables: ReadonlyWatchable<any>[] = [];

    // Create a getter function that tracks which watchables are accessed
    const get = <U>(watchable: ReadonlyWatchable<U>): U => {
      if (!sourceWatchables.includes(watchable)) {
        sourceWatchables.push(watchable);
      }
      return store.get(watchable.asAtom());
    };

    // Create the derived atom
    const derivedAtom = (watchable: Watchable<T>) =>
      atom((jotaiGet: Getter): T => {
        // Reset tracking for each evaluation
        sourceWatchables.length = 0;

        // Create a getter that uses jotai's get and tracks dependencies
        const trackedGet = <U>(watchable: ReadonlyWatchable<U>): U => {
          if (!sourceWatchables.includes(watchable)) {
            sourceWatchables.push(watchable);
          }
          return jotaiGet(watchable.asAtom());
        };

        const newValue = deriveFn(trackedGet);

        watchable.setLastUpdateTime(Date.now());

        return newValue;
      }) as WritableAtom<T, [T], unknown>;

    // For the initial call to establish dependencies
    deriveFn(get);

    return new Watchable<T>(derivedAtom, undefined, opts?.updater);
  }

  map<U>(cb: (t: T) => U): ReadonlyWatchable<U> {
    return Watchable.from((get) => cb(get(this)));
  }

  /**
   * Subscribe to changes to the watchable.
   * @param cb - The callback to call when the watchable changes.
   * @returns A function to unsubscribe from the watchable.
   */
  subscribe(cb: (t: T) => void): () => void {
    return store.sub(this._atom, () => cb(this.snapshot()));
  }

  snapshot() {
    return store.get(this._atom);
  }

  set(value: T) {
    const isEqualToPrevious = this._equalityFn?.(this.snapshot(), value);
    if (!isEqualToPrevious) {
      this.lastUpdateTime = Date.now();
      store.set(this._atom, this._updater?.(value));
    }
  }

  /**
   * Set the stored value without invoking the updater.
   * Use when syncing from an external source of truth (e.g. URL) that is already updated,
   * to avoid redundant side effects (e.g. writing back to the URL).
   */
  setFromSource(value: T) {
    const isEqualToPrevious = this._equalityFn?.(this.snapshot(), value);
    if (!isEqualToPrevious) {
      this.lastUpdateTime = Date.now();
      store.set(this._atom, value);
    }
  }

  asAtom(): Atom<T> {
    return this._atom;
  }

  private setLastUpdateTime(time: number) {
    this.lastUpdateTime = time;
  }
}

export type ReadonlyPollingWatchable<T> = ReadonlyWatchable<T> & {
  unsubscribe: () => void;
  restart: () => void;
};

export type WatchableGetter = <U>(w: ReadonlyWatchable<U>) => U;

export class PollingWatchable<T> extends Watchable<T> {
  private _timeoutId: ReturnType<typeof setTimeout> | undefined;
  private _subscribed = true;
  private _unsub: () => void;
  private _pollFn: (get: WatchableGetter) => Promise<T>;
  private _intervalMs: number;
  private _stopWhen: ((value: T) => boolean) | undefined;
  /** Incremented on each poll start; used to ignore stale responses from before restart() */
  private _pollGeneration = 0;

  protected constructor(
    baseAtom: (watchable: Watchable<T>) => WritableAtom<T, [T], unknown>,
    pollFn: (get: WatchableGetter) => Promise<T>,
    intervalMs: number,
    stopWhen?: (value: T) => boolean
  ) {
    super(baseAtom);

    this._pollFn = pollFn;
    this._intervalMs = intervalMs;
    this._stopWhen = stopWhen;

    this._runPollOnce();

    this._unsub = () => {
      this._subscribed = false;
      if (this._timeoutId !== undefined) {
        clearTimeout(this._timeoutId);
        this._timeoutId = undefined;
      }
    };
  }

  static create<T>(
    pollFn: (get: WatchableGetter) => Promise<T>,
    opts: {
      intervalMs: number;
      initialValue: T;
      stopWhen?: (value: T) => boolean;
    }
  ): ReadonlyPollingWatchable<T> {
    return new PollingWatchable<T>(
      () => atom(opts.initialValue),
      pollFn,
      opts.intervalMs,
      opts.stopWhen
    );
  }

  private _runPollOnce(): void {
    const generation = ++this._pollGeneration;
    const get: WatchableGetter = <U>(w: ReadonlyWatchable<U>): U =>
      store.get(w.asAtom()) as U;
    void this._pollFn(get)
      .then((value) => {
        if (!this._subscribed || generation !== this._pollGeneration) {
          return;
        }
        this.set(value);
        if (this._stopWhen?.(value) === true) {
          return;
        }
        this._scheduleNextPoll();
      })
      .catch(() => {
        if (this._subscribed && generation === this._pollGeneration) {
          this._scheduleNextPoll();
        }
      });
  }

  private _scheduleNextPoll(): void {
    if (!this._subscribed) {
      return;
    }
    this._timeoutId = setTimeout(() => {
      this._timeoutId = undefined;
      this._runPollOnce();
    }, this._intervalMs);
  }

  restart(): void {
    this._subscribed = true;
    if (this._timeoutId !== undefined) {
      clearTimeout(this._timeoutId);
      this._timeoutId = undefined;
    }

    this._runPollOnce();
  }

  unsubscribe() {
    this._unsub();
  }
}

export type ReadonlyQueryWatchable<
  TData = unknown,
  TError = Error
> = ReadonlyWatchable<QueryObserverResult<TData, TError>> & {
  unsubscribe(): void;
};

export type QueryWatchableOptions<TData = unknown, TError = Error> = Pick<
  QueryObserverOptions<unknown, TError, TData, unknown, QueryKey>,
  'queryKey'
> &
  Partial<
    Pick<
      QueryObserverOptions<unknown, TError, TData, unknown, QueryKey>,
      'select' | 'enabled'
    >
  >;

/**
 * Watchable that stays in sync with a TanStack Query cache entry for the given query key.
 * Uses QueryObserver under the hood; call unsubscribe() when done to release the observer.
 */
export class QueryWatchableImpl<TData = unknown, TError = Error>
  extends Watchable<QueryObserverResult<TData, TError>>
  implements ReadonlyQueryWatchable<TData, TError>
{
  private _unsub: () => void;

  private constructor(
    baseAtom: (
      watchable: Watchable<QueryObserverResult<TData, TError>>
    ) => WritableAtom<
      QueryObserverResult<TData, TError>,
      [QueryObserverResult<TData, TError>],
      unknown
    >,
    getUnsub: (w: Watchable<QueryObserverResult<TData, TError>>) => () => void
  ) {
    super(baseAtom);
    this._unsub = getUnsub(this);
  }

  static for<TData = unknown, TError = Error>(
    queryClient: QueryClient,
    options: QueryWatchableOptions<TData, TError>
  ): ReadonlyQueryWatchable<TData, TError> {
    const { queryKey, ...rest } = options;
    const observer = new QueryObserver(queryClient, {
      queryKey,
      ...rest,
    });
    const initialResult = observer.getCurrentResult();
    return new QueryWatchableImpl<TData, TError>(
      () => atom(initialResult),
      (w) => observer.subscribe((result) => w.set(result))
    );
  }

  unsubscribe(): void {
    this._unsub();
  }
}

export const QueryWatchable = {
  for: QueryWatchableImpl.for,
};

export type ReadonlyEventWatchable<T> = ReadonlyWatchable<T> & {
  refresh: (payload?: unknown) => Promise<void>;
  unsubscribe: () => void;
};

export type EventWatchableFallback<T> = {
  /** Fetches the value when events are not received (e.g. WebSocket down). */
  fn: () => Promise<T>;
  /** Poll interval in ms. Defaults to 3000. */
  interval?: number;
};

export type ReconcileOptions = {
  /** Poll fn(undefined) on this cadence (ms) to self-heal after missed events. */
  cadence: number;
};

export type EventWatchableOptions<T, P = unknown> = {
  /** Event type(s) to subscribe to. When multiple, refreshes on any. Requires useEvents with these types somewhere in the tree. */
  event?: string;
  events?: string[];
  initialValue: T;
  /** Fetches the value. Receives the event payload when triggered by an event, undefined on runOnMount or manual refresh(); get(w) reads current value of a watchable (like Watchable.from(get => ...)). */
  fn: (payload: P | undefined, get: WatchableGetter) => Promise<T>;
  /** If true (default), fetches on creation. */
  runOnMount?: boolean;
  /** When provided, only refresh when this returns true for the event payload. */
  filter?: (payload: P) => boolean;
  /** Override for testing. Defaults to broadcast event bus. */
  subscribe?: (event: string, cb: (payload?: unknown) => void) => () => void;
  /** When provided, skips store update when this returns true (prevents redundant rerenders when event data equals current state). */
  equalityFn?: (a: T, b: T) => boolean;
  /** When provided, polls when WebSocket is disconnected (fallback for WebSocket failures). */
  fallback?: EventWatchableFallback<T>;
  /** When provided, polls fn(undefined) on the given cadence (ms) to self-heal if events were missed. */
  reconcile?: ReconcileOptions;
  /** Override for testing. Defaults to broadcast event bus connection status. */
  subscribeToConnectionStatus?: (
    cb: (connected: boolean) => void
  ) => () => void;
  /** When provided, debounces event-triggered refreshes by this many ms (trailing edge). Useful when events fire in rapid bursts. */
  debounceMs?: number;
  /** When true, only runs one refresh at a time and queues the latest follow-up refresh while in flight. */
  serializeRefreshes?: boolean;
  /** Queue strategy while a serialized refresh is in flight. `latest` keeps one trailing refresh, `discard` keeps only one trailing refresh and always replaces it with the newest trigger. */
  serializeQueueStrategy?: 'latest' | 'discard';
};

const FALLBACK_POLL_INTERVAL_MS = 3000;

export class EventWatchableImpl<T, P = unknown>
  extends Watchable<T>
  implements ReadonlyEventWatchable<T>
{
  private _unsub: () => void;
  private _fn: (payload: P | undefined, get: WatchableGetter) => Promise<T>;
  /** Incremented on each refresh start; used to ignore stale responses from concurrent calls */
  private _refreshGeneration = 0;
  private _fallback: EventWatchableFallback<T> | undefined;
  private _fallbackTimerId: ReturnType<typeof setTimeout> | null = null;
  /** Latest push-stream connection state; the fallback only runs while false. */
  private _isConnected = false;
  private _unsubConnectionStatus: (() => void) | undefined;
  private _reconcile: ReconcileOptions | undefined;
  private _reconcileTimerId: ReturnType<typeof setTimeout> | undefined =
    undefined;
  private _debounceTimerId: ReturnType<typeof setTimeout> | undefined =
    undefined;
  private _debouncedPayload: P | undefined = undefined;
  private _serializeRefreshes: boolean;
  private _serializeQueueStrategy: 'latest' | 'discard';
  private _isRefreshing = false;
  private _queuedRefreshPayload: P | undefined = undefined;
  private _hasQueuedRefresh = false;

  protected constructor(
    baseAtom: (watchable: Watchable<T>) => WritableAtom<T, [T], unknown>,
    opts: EventWatchableOptions<T, P>
  ) {
    super(baseAtom, opts.equalityFn);
    this._fn = opts.fn;
    this._fallback = opts.fallback;
    this._reconcile = opts.reconcile;
    this._serializeRefreshes = opts.serializeRefreshes ?? false;
    this._serializeQueueStrategy = opts.serializeQueueStrategy ?? 'latest';

    const eventList =
      opts.events ?? (opts.event !== undefined ? [opts.event] : []);
    if (eventList.length === 0) {
      throw new Error('EventWatchable requires event or events');
    }

    const sub = opts.subscribe ?? subscribeToBroadcastEvent;
    const unsubs = eventList.map((ev) =>
      sub(ev, (payload) => {
        if (opts.filter !== undefined && !opts.filter(payload as P)) {
          return;
        }
        if (opts.debounceMs !== undefined) {
          this._debouncedPayload = payload as P;
          if (this._debounceTimerId !== undefined) {
            clearTimeout(this._debounceTimerId);
          }
          this._debounceTimerId = setTimeout(() => {
            this._debounceTimerId = undefined;
            const p = this._debouncedPayload;
            this._debouncedPayload = undefined;
            this._refreshInBackground(p);
          }, opts.debounceMs);
        } else {
          this._refreshInBackground(payload as P);
        }
      })
    );
    const subToConnectionStatus =
      opts.subscribeToConnectionStatus ?? subscribeToConnectionStatusDefault;

    this._unsub = () => {
      this._isDisposed = true;
      unsubs.forEach((u) => u());
      this._unsubConnectionStatus?.();
      this._clearFallbackTimer();
      this._clearReconcileTimer();
      this._clearDebounceTimer();
    };

    if (opts.runOnMount !== false) {
      this._refreshInBackground(undefined);
    }

    if (this._fallback !== undefined) {
      this._unsubConnectionStatus = subToConnectionStatus((connected) => {
        this._isConnected = connected;
        if (connected) {
          this._clearFallbackTimer();
        } else {
          this._scheduleFallbackPoll();
        }
      });
    }

    if (this._reconcile !== undefined) {
      this._scheduleReconcile();
    }
  }

  private _isDisposed = false;

  private _refreshInBackground(payload?: P): void {
    void this._refresh(payload).catch(() => {
      // Avoid uncaught promise rejections from background refresh triggers.
      // Callers that need error handling should await refresh().
    });
  }

  private _clearReconcileTimer(): void {
    if (this._reconcileTimerId !== undefined) {
      clearTimeout(this._reconcileTimerId);
      this._reconcileTimerId = undefined;
    }
  }

  private _clearDebounceTimer(): void {
    if (this._debounceTimerId !== undefined) {
      clearTimeout(this._debounceTimerId);
      this._debounceTimerId = undefined;
    }
  }

  /** Schedules next reconcile poll (fn(undefined)) after cadence ms to self-heal after missed events. */
  private _scheduleReconcile(): void {
    if (this._isDisposed) {
      return;
    }
    const reconcile = this._reconcile;
    if (reconcile === undefined) {
      return;
    }
    this._reconcileTimerId = setTimeout(() => {
      this._reconcileTimerId = undefined;
      if (this._isDisposed) {
        return;
      }
      void this._refresh(undefined)
        .catch(() => {
          // Reconcile is best-effort; keep cadence even on refresh failures.
        })
        .finally(() => {
          if (!this._isDisposed) {
            this._scheduleReconcile();
          }
        });
    }, reconcile.cadence);
  }

  private _clearFallbackTimer(): void {
    if (this._fallbackTimerId !== null) {
      clearTimeout(this._fallbackTimerId);
      this._fallbackTimerId = null;
    }
  }

  /**
   * Runs the fallback poll loop while the push stream is down.
   *
   * Connection state is re-checked at three points — before scheduling, when
   * the timer fires, and again before chaining the next tick — because a
   * reconnect can land at any of them. Without the check in the chaining step
   * a reconnect that arrives mid-fetch is undone by `finally` immediately
   * rescheduling, leaving the watchable polling forever on a healthy socket.
   *
   * Clearing first also keeps repeated disconnects from stacking parallel
   * loops onto the single `_fallbackTimerId` slot.
   */
  private _scheduleFallbackPoll(): void {
    const fallback = this._fallback;
    if (fallback === undefined) {
      return;
    }
    this._clearFallbackTimer();
    if (this._isConnected || this._isDisposed) {
      return;
    }
    const interval = fallback.interval ?? FALLBACK_POLL_INTERVAL_MS;
    this._fallbackTimerId = setTimeout(() => {
      this._fallbackTimerId = null;
      if (this._isConnected || this._isDisposed) {
        return;
      }
      void fallback
        .fn()
        .then((value) => {
          this.set(value);
        })
        .catch(() => {
          // Best-effort; keep polling while the push stream is still down.
        })
        .finally(() => {
          this._scheduleFallbackPoll();
        });
    }, interval);
  }

  static for<T, P = unknown>(
    opts: EventWatchableOptions<T, P>
  ): ReadonlyEventWatchable<T> {
    return new EventWatchableImpl<T, P>(() => atom(opts.initialValue), opts);
  }

  private async _runRefresh(payload?: P): Promise<void> {
    const generation = ++this._refreshGeneration;
    const get: WatchableGetter = <U>(w: ReadonlyWatchable<U>): U =>
      store.get(w.asAtom()) as U;
    const value = await this._fn(payload, get);
    if (generation !== this._refreshGeneration) {
      return;
    }
    this.set(value);
  }

  private async _refresh(payload?: P): Promise<void> {
    if (!this._serializeRefreshes) {
      await this._runRefresh(payload);
      return;
    }

    if (this._isRefreshing) {
      if (
        this._serializeQueueStrategy === 'discard' &&
        this._hasQueuedRefresh === false
      ) {
        this._queuedRefreshPayload = payload;
        this._hasQueuedRefresh = true;
        return;
      }
      this._queuedRefreshPayload = payload;
      this._hasQueuedRefresh = true;
      return;
    }

    this._isRefreshing = true;
    let currentPayload = payload;
    try {
      do {
        this._queuedRefreshPayload = undefined;
        this._hasQueuedRefresh = false;
        await this._runRefresh(currentPayload);
        currentPayload = this._queuedRefreshPayload;
      } while (this._hasQueuedRefresh);
    } finally {
      this._isRefreshing = false;
      this._queuedRefreshPayload = undefined;
      this._hasQueuedRefresh = false;
    }
  }

  refresh(payload?: unknown): Promise<void> {
    return this._refresh(payload as P);
  }

  unsubscribe(): void {
    this._unsub();
  }
}

export const EventWatchable = {
  for: EventWatchableImpl.for,
};

export type TwoWayEventWatchable<T> = ReadonlyEventWatchable<T> & {
  setOptimistic: (value: T) => Promise<void>;
};

export type TwoWayEventWatchableOptions<T, P = unknown> = EventWatchableOptions<
  T,
  P
> & {
  /** Persist optimistic value. Called when setOptimistic is used. If it throws, we rollback to previous value. */
  persist: (value: T) => Promise<void>;
};

export class TwoWayEventWatchableImpl<T, P = unknown>
  implements TwoWayEventWatchable<T>
{
  private _inner: EventWatchableImpl<T, P>;
  private _persist: (value: T) => Promise<void>;

  private constructor(
    inner: EventWatchableImpl<T, P>,
    persist: (value: T) => Promise<void>
  ) {
    this._inner = inner;
    this._persist = persist;
  }

  get lastUpdateTime(): number {
    return this._inner.lastUpdateTime;
  }

  static for<T, P = unknown>(
    opts: TwoWayEventWatchableOptions<T, P>
  ): TwoWayEventWatchable<T> {
    const { persist, ...eventOpts } = opts;
    const inner = EventWatchableImpl.for(eventOpts) as EventWatchableImpl<T, P>;
    return new TwoWayEventWatchableImpl<T, P>(inner, persist);
  }

  async setOptimistic(value: T): Promise<void> {
    const previous = this._inner.snapshot();
    (this._inner as Watchable<T>).set(value);
    try {
      await this._persist(value);
    } catch {
      (this._inner as Watchable<T>).set(previous);
      throw new Error('Persist failed, rolled back');
    }
  }

  refresh(payload?: unknown): Promise<void> {
    return this._inner.refresh(payload);
  }

  unsubscribe(): void {
    this._inner.unsubscribe();
  }

  map<U>(cb: (t: T) => U): ReadonlyWatchable<U> {
    return this._inner.map(cb);
  }

  snapshot(): T {
    return this._inner.snapshot();
  }

  subscribe(cb: (t: T) => void): () => void {
    return this._inner.subscribe(cb);
  }

  asAtom(): Atom<T> {
    return this._inner.asAtom();
  }
}

export const TwoWayEventWatchable = {
  for: TwoWayEventWatchableImpl.for,
};

type Storage<T> = {
  getItem: (key: string, defaultValue: T) => T;
  setItem: (key: string, value: T) => Promise<void>;
  subscribe: (key: string, listener: (value: T) => void) => () => void;
};

export class PersistentWatchable<T> extends Watchable<T> {
  constructor(
    baseAtom: (watchable: Watchable<T>) => WritableAtom<T, [T], unknown>,
    storage: Storage<T>,
    storageKey: string,
    equalityFn?: (a: T, b: T) => boolean,
    updater?: (value: T) => T
  ) {
    super(baseAtom, equalityFn ?? ((a, b) => a === b), (x) => {
      const next = updater?.(x) ?? x;
      void storage.setItem(storageKey, next);
      return next;
    });
  }

  static fromStorage<T>(props: {
    storage: Storage<T>;
    storageKey: string;
    defaultValue: T;
    map?: (value: T) => T;
    equalityFn?: (a: T, b: T) => boolean;
    updater?: (value: T) => T;
  }): PersistentWatchable<T> {
    const map = props.map ?? ((x) => x);
    return new PersistentWatchable<T>(
      (_) =>
        atom(map(props.storage.getItem(props.storageKey, props.defaultValue))),
      props.storage,
      props.storageKey,
      props.equalityFn,
      props.updater
    );
  }
}

export type ReadonlyDebouncedWatchable<T> = ReadonlyWatchable<T> & {
  /** Stops trailing the source and drops any pending emit. */
  unsubscribe: () => void;
  /** Emits the source's current value now instead of waiting out the delay. */
  flush: () => void;
  /** Drops a pending emit; the last emitted value stays put. */
  cancel: () => void;
};

export type DebouncedWatchableOptions<T> = {
  /** Trailing-edge delay: the source must be quiet this long before the value lands. */
  delayMs: number;
  /** When provided, skips the store update (and subscriber notification) when this returns true. */
  equalityFn?: (a: T, b: T) => boolean;
};

/**
 * Trails another watchable, emitting only once the source has been quiet for
 * `delayMs`. Lets a caller keep the value that updates on every input event
 * (what a text box renders) separate from the settled one that drives expensive
 * work (a fetch, a history rewrite), without hand-rolled timers in a
 * `useEffect`.
 *
 * Trailing edge only, and the emit reads the source's snapshot when the timer
 * fires rather than the value that scheduled it — so a burst collapses into one
 * emit of the newest value, and a value superseded mid-delay never lands.
 *
 * Call `unsubscribe()` when done to release the source subscription and clear
 * any pending timer.
 */
export class DebouncedWatchableImpl<T>
  extends Watchable<T>
  implements ReadonlyDebouncedWatchable<T>
{
  private _source: ReadonlyWatchable<T>;
  private _delayMs: number;
  private _timerId: ReturnType<typeof setTimeout> | undefined = undefined;
  private _unsub: () => void;

  protected constructor(
    baseAtom: (watchable: Watchable<T>) => WritableAtom<T, [T], unknown>,
    source: ReadonlyWatchable<T>,
    opts: DebouncedWatchableOptions<T>
  ) {
    super(baseAtom, opts.equalityFn);
    this._source = source;
    this._delayMs = opts.delayMs;
    this._unsub = source.subscribe(() => {
      this._schedule();
    });
  }

  static for<T>(
    source: ReadonlyWatchable<T>,
    opts: DebouncedWatchableOptions<T>
  ): ReadonlyDebouncedWatchable<T> {
    return new DebouncedWatchableImpl<T>(
      () => atom(source.snapshot()),
      source,
      opts
    );
  }

  private _schedule(): void {
    this._clearTimer();
    this._timerId = setTimeout(() => {
      this._timerId = undefined;
      this.set(this._source.snapshot());
    }, this._delayMs);
  }

  private _clearTimer(): void {
    if (this._timerId !== undefined) {
      clearTimeout(this._timerId);
      this._timerId = undefined;
    }
  }

  flush(): void {
    this._clearTimer();
    this.set(this._source.snapshot());
  }

  cancel(): void {
    this._clearTimer();
  }

  unsubscribe(): void {
    this._clearTimer();
    this._unsub();
  }
}

export const DebouncedWatchable = {
  for: DebouncedWatchableImpl.for,
};

export function useWatchableValue<T>(x: ReadonlyWatchable<T>): T {
  const [val] = useAtom(x.asAtom());
  return val;
}

export const useWatchableUpdatedAt = (x: ReadonlyWatchable<any>): number => {
  const [_val] = useAtom(x.asAtom());

  return x.lastUpdateTime;
};

export const JotaiProvider = ({ children }: { children: React.ReactNode }) => {
  return Provider({ store, children });
};

export class WatchableRecord<T extends Record<string, any>>
  implements ReadonlyWatchable<T>
{
  private _keysAtom: WritableAtom<string[], [string[]], unknown>;
  private _values: Map<string, Watchable<any>>;
  lastUpdateTime: number = Date.now();

  private constructor(initialValue: T) {
    // Create an atom to track the keys
    this._keysAtom = atom(Object.keys(initialValue));
    this._values = new Map();

    // Initialize watchables for each key
    Object.entries(initialValue).forEach(([key, value]) => {
      this._values.set(key, Watchable.fromValue(value));
    });
  }

  snapshot(): T {
    return Object.fromEntries(
      [...this._values.entries()].map(([key, value]) => [key, value.snapshot()])
    ) as T;
  }

  subscribe(cb: (t: T) => void): () => void {
    return store.sub(this._keysAtom, () => cb(this.snapshot()));
  }

  static fromValue<T extends Record<string, any>>(
    value: T
  ): WatchableRecord<T> {
    return new WatchableRecord<T>(value);
  }

  getItem<K extends keyof T>(key: K): Watchable<T[K]> {
    const watchable = this._values.get(key as string);
    if (!watchable) {
      throw new Error(`Key ${String(key)} not found in record`);
    }
    return watchable as Watchable<T[K]>;
  }

  setItem<K extends keyof T>(key: K, value: T[K]): void {
    const watchable = this._values.get(key as string);
    if (watchable) {
      watchable.set(value);
    } else {
      // New key
      this._values.set(key as string, Watchable.fromValue(value));
      const currentKeys = store.get(this._keysAtom);
      store.set(this._keysAtom, [...currentKeys, key as string]);
    }
    this.lastUpdateTime = Date.now();
  }

  map<U>(cb: (t: T) => U): ReadonlyWatchable<U> {
    return Watchable.from((get) => cb(get(this)));
  }

  delete<K extends keyof T>(key: K): void {
    if (this._values.has(key as string)) {
      this._values.delete(key as string);
      const currentKeys = store.get(this._keysAtom);
      store.set(
        this._keysAtom,
        currentKeys.filter((k) => k !== key)
      );
    }
    this.lastUpdateTime = Date.now();
  }

  asAtom(): Atom<T> {
    return atom((get) => {
      const keys = get(this._keysAtom);
      const result = {} as T;
      keys.forEach((key) => {
        const watchable = this._values.get(key);
        if (watchable) {
          result[key as keyof T] = get(watchable.asAtom());
        }
      });
      return result;
    });
  }

  // Get all current keys
  keys(): string[] {
    return store.get(this._keysAtom);
  }

  // Check if a key exists
  has<K extends keyof T>(key: K): boolean {
    return this._values.has(key as string);
  }
}

export type MixedSourceWatchable<T> = ReadonlyWatchable<T> & {
  set(value: T): void;
  unsubscribe(): void;
};

export type MixedSourceOptions<T> = {
  /**
   * When derived (streamed) value changes, only accept it if this returns true.
   * If false, keep the user's override. Use to reconcile: e.g. if streamed updatedAt
   * is older than lastUserEditTime, return false to preserve user edits.
   */
  shouldAcceptDerived?: (
    derived: T,
    override: T | null,
    lastUserEditTime: number,
    get: <U>(watchable: ReadonlyWatchable<U>) => U
  ) => boolean;
};

/**
 * A watchable that supports both derived and manual updates.
 * Derived updates (from dependency changes) take precedence over manual updates.
 * Manual updates persist until a dependency changes again.
 *
 * Value trace example: dep changes → derived value → user calls .set() → manual value → dep changes again → derived value
 *
 * With shouldAcceptDerived: when derived changes, override is kept if the callback returns false
 * (e.g. when streamed data is older than user's last edit).
 */
export class MixedSourceWatchableImpl<T> implements MixedSourceWatchable<T> {
  private _derivedAtom: Atom<T>;
  private _overrideAtom: WritableAtom<T | null, [T | null], unknown>;
  private _displayAtom: Atom<T>;
  private _unsubDerived: () => void;
  private _lastUserEditTime: number = 0;

  lastUpdateTime: number = Date.now();

  private constructor(
    deriveFn: (get: <U>(watchable: ReadonlyWatchable<U>) => U) => T,
    private readonly options?: MixedSourceOptions<T>
  ) {
    const sourceWatchables: ReadonlyWatchable<unknown>[] = [];

    const get = <U>(watchable: ReadonlyWatchable<U>): U => {
      if (!sourceWatchables.includes(watchable as ReadonlyWatchable<unknown>)) {
        sourceWatchables.push(watchable as ReadonlyWatchable<unknown>);
      }
      return store.get(watchable.asAtom());
    };

    // Establish initial dependencies
    deriveFn(get);

    this._overrideAtom = atom<T | null>(null);

    this._derivedAtom = atom((jotaiGet: Getter): T => {
      sourceWatchables.length = 0;
      const trackedGet = <U>(watchable: ReadonlyWatchable<U>): U => {
        if (
          !sourceWatchables.includes(watchable as ReadonlyWatchable<unknown>)
        ) {
          sourceWatchables.push(watchable as ReadonlyWatchable<unknown>);
        }
        return jotaiGet(watchable.asAtom());
      };
      return deriveFn(trackedGet);
    });

    this._displayAtom = atom((jotaiGet: Getter): T => {
      const override = jotaiGet(this._overrideAtom);
      if (override !== null) {
        return override;
      }
      return jotaiGet(this._derivedAtom);
    });

    // When derived value changes, optionally reconcile: only clear override if shouldAcceptDerived allows
    const read = <U>(watchable: ReadonlyWatchable<U>): U =>
      store.get(watchable.asAtom()) as U;
    this._unsubDerived = store.sub(this._derivedAtom, () => {
      const derived = store.get(this._derivedAtom);
      const override = store.get(this._overrideAtom);
      const shouldAccept =
        this.options?.shouldAcceptDerived === undefined ||
        this.options.shouldAcceptDerived(
          derived,
          override,
          this._lastUserEditTime,
          read
        );
      if (shouldAccept) {
        store.set(this._overrideAtom, null);
        this.lastUpdateTime = Date.now();
      }
    });
  }

  static from<T>(
    deriveFn: (get: <U>(watchable: ReadonlyWatchable<U>) => U) => T,
    options?: MixedSourceOptions<T>
  ): MixedSourceWatchable<T> {
    return new MixedSourceWatchableImpl<T>(deriveFn, options);
  }

  set(value: T): void {
    this._lastUserEditTime = Date.now();
    this.lastUpdateTime = Date.now();
    store.set(this._overrideAtom, value);
  }

  map<U>(cb: (t: T) => U): ReadonlyWatchable<U> {
    return Watchable.from((get) => cb(get(this)));
  }

  snapshot(): T {
    return store.get(this._displayAtom);
  }

  subscribe(cb: (t: T) => void): () => void {
    return store.sub(this._displayAtom, () => cb(this.snapshot()));
  }

  asAtom(): Atom<T> {
    return this._displayAtom;
  }

  unsubscribe(): void {
    this._unsubDerived();
  }
}

/** Factory for creating MixedSourceWatchables */
export const MixedSourceWatchable = {
  from: MixedSourceWatchableImpl.from,
};

export class StalenessWatchable {
  static forValue(
    value: ReadonlyWatchable<any>,
    deps: ReadonlyWatchable<any>[]
  ): ReadonlyWatchable<StaleState> {
    return Watchable.from((get) => {
      for (const dep of deps) {
        get(dep);
        get(value);
        if (dep.lastUpdateTime > value.lastUpdateTime) {
          return 'stale';
        }
      }
      return 'ok';
    });
  }
}
